<template>
  <div class="agent-page">
    <Card>
      <div class="toolbar">
        <Input
          v-model="keyword"
          clearable
          class="keyword-input"
          placeholder="代理商名称、联系人或联系电话"
          @on-enter="handleButtonSearch"
          @on-clear="handleSearchClear"
        >
          <span slot="prepend">关键字</span>
        </Input>
        <div class="toolbar-actions">
          <Button icon="md-refresh" @click="resetSearch">重置</Button>
          <Button
            type="primary"
            icon="ios-search"
            :loading="tableLoading"
            @click="handleButtonSearch"
          >
            查询
          </Button>
          <Button
            v-if="isSuperAdmin"
            type="primary"
            icon="md-add"
            @click="showAddModal"
          >
            新增代理商
          </Button>
        </div>
      </div>

      <Table
        border
        :loading="tableLoading"
        :columns="displayColumns"
        :data="tableData"
        no-data-text="暂无代理商"
      />

      <div class="pagination-wrap">
        <Page
          :current="page"
          :total="total"
          :page-size="pageSize"
          :page-size-opts="[10, 20, 50, 100]"
          show-sizer
          show-elevator
          show-total
          @on-change="changePage"
          @on-page-size-change="changePageSize"
        />
      </div>
    </Card>

    <Modal
      v-model="formModal"
      :title="formMode === 'edit' ? '编辑代理商' : '新增代理商'"
      :width="680"
      :mask-closable="false"
      :closable="!formSubmitting"
      footer-hide
      @on-visible-change="handleFormVisibleChange"
    >
      <div class="modal-loading-wrap">
        <Spin v-if="formLoading" fix />
        <agent-form
          v-else
          ref="agentForm"
          :key="formKey"
          :mode="formMode"
          :initial-data="formInitialData"
          :submitting="formSubmitting"
          @on-ok="handleFormSubmit"
        />
      </div>
    </Modal>

    <Modal
      v-model="detailModal"
      :width="720"
      :mask-closable="false"
      title="代理商详情"
      footer-hide
      @on-visible-change="handleDetailVisibleChange"
    >
      <div class="modal-loading-wrap detail-loading-wrap">
        <Spin v-if="detailLoading" fix />
        <div v-else class="detail-grid">
          <div class="detail-item detail-item-full">
            <span class="detail-label">代理商名称</span>
            <span>{{ displayValue(detailData.name) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">负责人账号</span>
            <span>{{ displayValue(detailData.ownerUserName) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">代理商归属</span>
            <span>{{ relationLabel(detailData) }}</span>
            <Button v-if="detailData.parentAgentId" type="text" @click="showDetail({ id: detailData.parentAgentId })">查看上级</Button>
          </div>
          <div class="detail-item">
            <span class="detail-label">负责人 ID</span>
            <span>{{ displayValue(detailData.ownerUserId) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">联系人</span>
            <span>{{ displayValue(detailData.contactName) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">联系电话</span>
            <span>{{ displayValue(detailData.phone) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">状态</span>
            <span>
              <Tag :color="detailData.status === 1 ? 'green' : 'default'">
                {{ detailData.status === 1 ? '启用' : '禁用' }}
              </Tag>
            </span>
          </div>
          <div class="detail-item">
            <span class="detail-label">创建时间</span>
            <span>{{ displayValue(detailData.createTime) }}</span>
          </div>
          <div class="detail-item detail-item-full">
            <span class="detail-label">更新时间</span>
            <span>{{ displayValue(detailData.updateTime) }}</span>
          </div>
        </div>
      </div>
    </Modal>
    <Modal v-model="childrenModal" :title="`${childrenParent.name || ''} · 直属下级`" :width="900" footer-hide @on-visible-change="handleChildrenVisibleChange">
      <Alert show-icon>这里只展示直属下级，上级代管权限不跨级。</Alert>
      <Input v-model="childrenKeyword" clearable placeholder="筛选下级名称或负责人" @on-change="childrenPage = 1" />
      <Table :columns="childrenColumns" :data="visibleChildren" :loading="childrenLoading" no-data-text="暂无直属下级代理商" />
      <Page :current="childrenPage" :total="filteredChildren.length" :page-size="20" show-total @on-change="childrenPage = $event" />
    </Modal>
    <agent-parent-change :visible.sync="parentChangeVisible" :agent="parentChangeAgent" @saved="handleParentChangeSaved" />
  </div>
</template>

<script>
import AgentForm from './form.vue'
import AgentParentChange from './parent-change.vue'
import responsiveTable from '@/mixins/responsive-table'
import {
  addAgent,
  buildAgentPayload,
  deleteAgent,
  editAgent,
  getAgentInfo,
  getAgentList,
  getAgentOwnerCreationContext,
  getDirectChildAgents
} from '@/api/agent'

export default {
  name: 'AgentManagement',
  components: {
    AgentForm,
    AgentParentChange
  },
  mixins: [responsiveTable],
  computed: {
    displayColumns () {
      return this.isCompactViewport ? this.columns.filter(column => !column.mobileHidden) : this.columns
    },
    isSuperAdmin () {
      const access = this.$store.state.user.access
      return Array.isArray(access) && (
        access.includes('admin') || access.includes('super_admin')
      )
    },
    filteredChildren () {
      const keyword = this.childrenKeyword.trim().toLowerCase()
      return this.directChildren.filter(item => !keyword || `${item.name} ${item.ownerUserName}`.toLowerCase().includes(keyword))
    },
    visibleChildren () {
      return this.filteredChildren.slice((this.childrenPage - 1) * 20, this.childrenPage * 20)
    }
  },
  data () {
    return {
      page: 1,
      pageSize: 20,
      total: 0,
      keyword: '',
      tableData: [],
      tableLoading: false,
      formModal: false,
      formMode: 'add',
      formKey: 0,
      formLoading: false,
      formSubmitting: false,
      formInitialData: null,
      editingAgentId: null,
      detailModal: false,
      detailLoading: false,
      detailData: {},
      parentChangeVisible: false,
      parentChangeAgent: null,
      childrenModal: false,
      childrenParent: {},
      directChildren: [],
      childrenLoading: false,
      childrenKeyword: '',
      childrenPage: 1,
      childrenRequestId: 0,
      childrenColumns: [
        { title: '代理商名称', key: 'name', minWidth: 150 },
        { title: '负责人', key: 'ownerUserName', minWidth: 120 },
        { title: '状态', width: 80, render: (h, params) => h('span', params.row.status === 1 ? '启用' : '禁用') },
        {
          title: '操作',
          width: 160,
          render: (h, params) => h('div', [
            h('Button', { props: { type: 'text' }, on: { click: () => this.openChildDetail(params.row) } }, '详情'),
            h('Button', { props: { type: 'text', disabled: params.row.status !== 1 }, on: { click: () => this.showChildren(params.row) } }, '直属下级')
          ])
        }
      ],
      statusUpdatingIds: [],
      deletingIds: [],
      statusConfirmingIds: [],
      listRequestId: 0,
      formRequestId: 0,
      detailRequestId: 0,
      columns: [
        {
          title: '代理商名称',
          key: 'name',
          minWidth: 150,
          fixed: 'left',
          ellipsis: true,
          tooltip: true
        },
        {
          title: '代理商归属',
          key: 'parentAgentId',
          minWidth: 160,
          render: (h, params) => h('span', this.relationLabel(params.row))
        },
        {
          title: '负责人账号',
          key: 'ownerUserName',
          minWidth: 140,
          mobileHidden: true,
          ellipsis: true,
          tooltip: true,
          render: (h, params) => h('span', this.displayValue(params.row.ownerUserName))
        },
        {
          title: '联系人',
          key: 'contactName',
          minWidth: 110,
          ellipsis: true,
          tooltip: true,
          render: (h, params) => h('span', this.displayValue(params.row.contactName))
        },
        {
          title: '联系电话',
          key: 'phone',
          minWidth: 135,
          render: (h, params) => h('span', this.maskPhone(params.row.phone))
        },
        {
          title: '状态',
          key: 'status',
          width: 105,
          render: (h, params) => h('i-switch', {
            props: {
              value: params.row.status === 1,
              disabled: !this.isSuperAdmin ||
                this.statusConfirmingIds.includes(params.row.id) ||
                this.statusUpdatingIds.includes(params.row.id),
              loading: this.statusUpdatingIds.includes(params.row.id),
              size: 'large',
              beforeChange: () => this.confirmStatusChange(params.row)
            }
          }, [
            h('span', { slot: 'open' }, '启用'),
            h('span', { slot: 'close' }, '禁用')
          ])
        },
        { title: '创建时间', key: 'createTime', minWidth: 165, mobileHidden: true },
        { title: '更新时间', key: 'updateTime', minWidth: 165, mobileHidden: true },
        {
          title: '操作',
          key: 'handle',
          width: 320,
          fixed: 'right',
          render: (h, params) => {
            const buttons = [
              h('Button', {
                props: { type: 'text', size: 'small', icon: 'ios-eye-outline' },
                on: { click: () => this.showDetail(params.row) }
              }, '查看')
            ]
            buttons.push(h('Button', {
              props: { type: 'text', size: 'small', disabled: params.row.status !== 1 },
              on: { click: () => this.showChildren(params.row) }
            }, '直属下级'))
            if (this.isSuperAdmin) {
              buttons.push(h('Button', {
                props: { type: 'text', size: 'small', icon: 'md-git-branch' },
                on: { click: () => this.showParentChange(params.row) }
              }, '调整上级'))
              buttons.push(h('Button', {
                props: { type: 'text', size: 'small', icon: 'md-create' },
                on: { click: () => this.showEditModal(params.row) }
              }, '编辑'))
              buttons.push(h('Button', {
                props: {
                  type: 'text',
                  size: 'small',
                  icon: 'md-trash',
                  loading: this.deletingIds.includes(params.row.id)
                },
                on: { click: () => this.handleDelete(params.row) }
              }, '删除'))
            }
            return h('div', { class: 'row-actions' }, buttons)
          }
        }
      ]
    }
  },
  methods: {
    showParentChange (row) {
      if (!this.isSuperAdmin) return
      this.parentChangeAgent = row
      this.parentChangeVisible = true
    },
    handleParentChangeSaved () {
      this.handleSearch()
      if (this.childrenModal) this.showChildren(this.childrenParent)
      if (this.detailModal && this.detailData.id) this.showDetail(this.detailData)
    },
    openChildDetail (row) {
      this.childrenModal = false
      this.handleChildrenVisibleChange(false)
      this.showDetail(row)
    },
    relationLabel (agent) {
      return agent.parentAgentId ? `下级代理商（上级 #${agent.parentAgentId}）` : '一级代理商'
    },
    async showChildren (row) {
      const requestId = ++this.childrenRequestId
      this.childrenParent = row
      this.childrenKeyword = ''
      this.childrenPage = 1
      this.directChildren = []
      this.childrenLoading = true
      this.childrenModal = true
      try {
        const res = await getDirectChildAgents(row.id)
        if (requestId !== this.childrenRequestId || !this.childrenModal) return
        if (res.data.code !== 200) throw new Error(res.data.msg || '获取直属下级失败')
        this.directChildren = res.data.data || []
      } catch (err) {
        if (requestId === this.childrenRequestId) this.$Message.error(this.getErrorMessage(err, err.message || '获取直属下级失败'))
      } finally {
        if (requestId === this.childrenRequestId) this.childrenLoading = false
      }
    },
    handleChildrenVisibleChange (visible) {
      if (!visible) {
        this.childrenRequestId += 1
        this.childrenLoading = false
      }
    },
    getErrorMessage (err, fallback) {
      return err && err.response && err.response.data && err.response.data.msg
        ? err.response.data.msg
        : fallback
    },
    displayValue (value) {
      return value === null || value === undefined || value === '' ? '-' : value
    },
    maskPhone (phone) {
      const value = String(phone || '')
      if (!value) return '-'
      if (value.length <= 7) return `${value.slice(0, 2)}****${value.slice(-2)}`
      return `${value.slice(0, 3)}****${value.slice(-4)}`
    },
    escapeHtml (value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
    refreshPermissions () {
      return this.$store.dispatch('getUserInfo').catch(() => {})
    },
    handleBusinessError (message, options = {}) {
      const text = message || options.fallback || '操作失败'
      this.$Message.error(text)
      if (options.formError && this.$refs.agentForm) {
        this.$refs.agentForm.handleServerError(text)
      }
      if (text === '只有超级管理员可以操作代理商') {
        this.formModal = false
        this.refreshPermissions()
      }
      if (text === '代理商不存在' && options.refreshList) {
        this.formModal = false
        this.detailModal = false
        this.handleSearch()
      }
      if (text === '无权查看该代理商') this.detailModal = false
    },
    resetSearch () {
      this.keyword = ''
      this.page = 1
      this.handleSearch()
    },
    handleSearchClear () {
      this.page = 1
      this.handleSearch()
    },
    handleButtonSearch () {
      this.page = 1
      this.handleSearch()
    },
    changePage (page) {
      if (page === this.page) return
      this.page = page
      this.handleSearch()
    },
    changePageSize (pageSize) {
      this.page = 1
      this.pageSize = pageSize
      this.handleSearch()
    },
    async handleSearch () {
      const requestId = ++this.listRequestId
      this.tableLoading = true
      try {
        const res = await getAgentList({
          page: this.page,
          pageSize: this.pageSize,
          keyword: this.keyword.trim() || undefined
        })
        if (requestId !== this.listRequestId) return
        if (res.data.code === 200) {
          const data = res.data.data || {}
          const records = data.records || []
          const total = Number(data.total) || 0
          const lastPage = Math.max(1, Math.ceil(total / this.pageSize))
          if (this.page > lastPage) {
            this.page = lastPage
            this.handleSearch()
            return
          }
          this.tableData = records
          this.total = total
        } else {
          this.tableData = []
          this.total = 0
          this.handleBusinessError(res.data.msg, { fallback: '查询代理商失败' })
        }
      } catch (err) {
        if (requestId !== this.listRequestId) return
        this.tableData = []
        this.total = 0
        this.$Message.error(this.getErrorMessage(err, '查询代理商失败'))
      } finally {
        if (requestId === this.listRequestId) this.tableLoading = false
      }
    },
    showAddModal () {
      this.formRequestId += 1
      this.formMode = 'add'
      this.formInitialData = null
      this.editingAgentId = null
      this.formLoading = false
      this.formSubmitting = false
      this.formKey += 1
      this.formModal = true
    },
    handleFormVisibleChange (visible) {
      if (visible || this.formSubmitting) return
      this.formRequestId += 1
      this.formLoading = false
    },
    async showEditModal (row) {
      const requestId = ++this.formRequestId
      this.formMode = 'edit'
      this.formInitialData = null
      this.editingAgentId = row.id
      this.formLoading = true
      this.formSubmitting = false
      this.formModal = true
      try {
        const res = await getAgentInfo(row.id)
        if (requestId !== this.formRequestId || !this.formModal) return
        if (res.data.code === 200 && res.data.data) {
          this.formInitialData = res.data.data
          this.formKey += 1
        } else {
          this.handleBusinessError(res.data.msg, {
            fallback: '获取代理商详情失败',
            refreshList: true
          })
          this.formModal = false
        }
      } catch (err) {
        if (requestId !== this.formRequestId) return
        this.$Message.error(this.getErrorMessage(err, '获取代理商详情失败'))
        this.formModal = false
      } finally {
        if (requestId === this.formRequestId) this.formLoading = false
      }
    },
    async handleFormSubmit (payload) {
      if (this.formSubmitting) return
      const ownerChanged = this.formMode === 'edit' &&
        Number(payload.ownerUserId) !== Number(this.formInitialData.ownerUserId)
      this.formSubmitting = true

      if (this.formMode === 'add') {
        const requestId = this.formRequestId
        const frozenPayload = { ...payload }
        try {
          const res = await getAgentOwnerCreationContext(frozenPayload.ownerUserId)
          if (requestId !== this.formRequestId || !this.formModal || this.formMode !== 'add') return
          if (res.data.code !== 200) {
            this.handleBusinessError(res.data.msg, { fallback: '检查负责人账号失败', formError: true })
            this.formSubmitting = false
            return
          }
          const reset = res.data.data.requiresRoleResetConfirmation === true
          this.$Modal.confirm({
            title: reset ? '确认清理原工厂角色并创建代理商' : '确认创建代理商',
            content: `<p>代理商：${this.escapeHtml(frozenPayload.name)}</p><p>负责人账号编号：${this.escapeHtml(frozenPayload.ownerUserId)}</p>` +
              (reset ? `<p>该账号已有 ${Number(res.data.data.companyRelationCount) || 0} 条工厂成员关系（另可能含旧工厂角色）。确认后将退出原工厂、清除原工厂角色，现有登录会失效。</p>` : '<p>该账号将成为代理商负责人，现有登录会失效。</p>'),
            okText: reset ? '确认清理并创建' : '确认创建',
            onOk: () => {
              if (requestId !== this.formRequestId || !this.formModal || this.formMode !== 'add') return
              return this.performFormSave({ ...frozenPayload, confirmGlobalRoleReset: reset })
            },
            onCancel: () => {
              if (requestId === this.formRequestId) this.formSubmitting = false
            }
          })
        } catch (err) {
          if (requestId === this.formRequestId) {
            this.handleBusinessError(this.getErrorMessage(err, '检查负责人账号失败'), { formError: true })
            this.formSubmitting = false
          }
        }
        return
      }

      if (!ownerChanged) {
        this.performFormSave(payload)
        return
      }

      this.$Modal.confirm({
        title: '确认更换负责人',
        content: '<p>更换后旧负责人的 agent 角色不会自动移除，是否继续？</p>',
        onOk: () => this.performFormSave(payload),
        onCancel: () => {
          this.formSubmitting = false
        }
      })
    },
    async performFormSave (payload) {
      try {
        const res = this.formMode === 'edit'
          ? await editAgent(this.editingAgentId, payload)
          : await addAgent(payload)
        if (res.data.code === 200) {
          const successText = this.formMode === 'edit' ? '代理商已更新' : '代理商创建成功'
          this.$Message.success(successText)
          this.formModal = false
          if (this.formMode === 'add') this.page = 1
          this.handleSearch()
        } else {
          this.handleBusinessError(res.data.msg, {
            fallback: this.formMode === 'edit' ? '更新代理商失败' : '创建代理商失败',
            refreshList: this.formMode === 'edit',
            formError: true
          })
        }
      } catch (err) {
        this.handleBusinessError(this.getErrorMessage(
          err,
          this.formMode === 'edit' ? '更新代理商失败' : '创建代理商失败'
        ), { formError: true })
      } finally {
        this.formSubmitting = false
      }
    },
    async showDetail (row) {
      const requestId = ++this.detailRequestId
      this.detailModal = true
      this.detailLoading = true
      this.detailData = {}
      try {
        const res = await getAgentInfo(row.id)
        if (requestId !== this.detailRequestId || !this.detailModal) return
        if (res.data.code === 200 && res.data.data) {
          this.detailData = res.data.data
        } else {
          this.handleBusinessError(res.data.msg, {
            fallback: '获取代理商详情失败',
            refreshList: true
          })
          this.detailModal = false
        }
      } catch (err) {
        if (requestId !== this.detailRequestId) return
        this.$Message.error(this.getErrorMessage(err, '获取代理商详情失败'))
        this.detailModal = false
      } finally {
        if (requestId === this.detailRequestId) this.detailLoading = false
      }
    },
    handleDetailVisibleChange (visible) {
      if (visible) return
      this.detailRequestId += 1
      this.detailLoading = false
    },
    confirmStatusChange (row) {
      if (this.statusConfirmingIds.includes(row.id) || this.statusUpdatingIds.includes(row.id)) {
        return new Promise(() => {})
      }
      const nextStatus = row.status === 1 ? 0 : 1
      const action = nextStatus === 1 ? '启用' : '禁用'
      this.statusConfirmingIds = [...this.statusConfirmingIds, row.id]

      return new Promise(resolve => {
        this.$Modal.confirm({
          title: `确认${action}`,
          content: `<p>确认${action}代理商“${this.escapeHtml(row.name)}”？</p>`,
          onOk: async () => {
            const success = await this.performStatusChange(row, nextStatus)
            this.statusConfirmingIds = this.statusConfirmingIds.filter(id => id !== row.id)
            if (!success) return
            resolve()
            window.setTimeout(() => {
              if (this._isDestroyed || this._isBeingDestroyed) return
              this.$set(row, 'status', nextStatus)
              this.handleSearch()
            }, 0)
          },
          onCancel: () => {
            this.statusConfirmingIds = this.statusConfirmingIds.filter(id => id !== row.id)
          }
        })
      })
    },
    async performStatusChange (row, nextStatus) {
      this.statusUpdatingIds = [...this.statusUpdatingIds, row.id]
      try {
        const infoRes = await getAgentInfo(row.id)
        if (infoRes.data.code !== 200 || !infoRes.data.data) {
          this.handleBusinessError(infoRes.data.msg, {
            fallback: '获取代理商详情失败',
            refreshList: true
          })
          return false
        }
        const payload = buildAgentPayload({
          ...infoRes.data.data,
          status: nextStatus
        })
        const editRes = await editAgent(row.id, payload)
        if (editRes.data.code === 200) {
          this.$Message.success(nextStatus === 1 ? '代理商已启用' : '代理商已禁用')
          return true
        } else {
          this.handleBusinessError(editRes.data.msg, {
            fallback: '更新代理商状态失败',
            refreshList: true
          })
          return false
        }
      } catch (err) {
        this.$Message.error(this.getErrorMessage(err, '更新代理商状态失败'))
        return false
      } finally {
        this.statusUpdatingIds = this.statusUpdatingIds.filter(id => id !== row.id)
      }
    },
    handleDelete (row) {
      if (this.deletingIds.includes(row.id)) return
      this.$Modal.confirm({
        title: '确认删除',
        content: `<p>确认删除代理商“${this.escapeHtml(row.name)}”？</p>`,
        onOk: () => this.performDelete(row)
      })
    },
    async performDelete (row) {
      this.deletingIds = [...this.deletingIds, row.id]
      try {
        const res = await deleteAgent(row.id)
        if (res.data.code === 200) {
          this.$Message.success('代理商已删除')
          if (this.tableData.length === 1 && this.page > 1) this.page -= 1
          this.handleSearch()
        } else {
          this.handleBusinessError(res.data.msg, {
            fallback: '删除代理商失败',
            refreshList: res.data.msg === '代理商不存在'
          })
        }
      } catch (err) {
        this.$Message.error(this.getErrorMessage(err, '删除代理商失败'))
      } finally {
        this.deletingIds = this.deletingIds.filter(id => id !== row.id)
      }
    }
  },
  mounted () {
    this.handleSearch()
  },
  beforeDestroy () {
    this.childrenRequestId += 1
    this.listRequestId += 1
    this.formRequestId += 1
    this.detailRequestId += 1
  }
}
</script>

<style lang="less" scoped>
.agent-page {
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
  }

  .keyword-input {
    width: 440px;
    max-width: 100%;
  }

  .toolbar-actions {
    display: flex;
    flex: none;
    gap: 8px;
  }

  .pagination-wrap {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
    padding-bottom: 2px;
    overflow-x: auto;
  }

  .modal-loading-wrap {
    position: relative;
    min-height: 190px;
  }

  .detail-loading-wrap {
    min-height: 120px;
  }

  .detail-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border-top: 1px solid #e8eaec;
    border-left: 1px solid #e8eaec;
  }

  .detail-item {
    display: grid;
    grid-template-columns: 110px minmax(0, 1fr);
    min-height: 46px;
    border-right: 1px solid #e8eaec;
    border-bottom: 1px solid #e8eaec;
  }

  .detail-item-full {
    grid-column: 1 / -1;
  }

  .detail-label {
    padding: 12px;
    background: #f8f8f9;
    color: #515a6e;
    font-weight: 600;
  }

  .detail-item > span:not(.detail-label) {
    min-width: 0;
    padding: 12px;
    overflow-wrap: anywhere;
  }
}

@media (max-width: 760px) {
  .agent-page {
    .toolbar {
      align-items: stretch;
      flex-direction: column;
    }

    .keyword-input {
      width: 100%;
    }

    .toolbar-actions {
      flex-wrap: wrap;
    }

    .detail-grid {
      grid-template-columns: 1fr;
    }

    .detail-item-full {
      grid-column: auto;
    }
  }
}
</style>
